import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CalculationFunction } from './types/calculation.type';
import { IIndicator } from '../relevant-information/interfaces/indicator.interface';
import { TranslateService } from '@ngx-translate/core';
import { SustainabilityService } from 'src/generated/graphql/sustainability/sustainability.graphql';

@Component({
  selector: 'app-indicators',
  templateUrl: './indicators.component.html',
  styleUrls: ['./indicators.component.scss']
})
export class IndicatorsComponent implements OnInit {
  @Input()
  pontuation: number;

  @Input()
  indicators: string[] = [];

  @Input()
  resourcesUpdated: IIndicator[];

  indicatorsTranslated: {value: string, name: string}[] = [];

  @Input()
  showAddIndicatorBtn: boolean = true;

  @Output()
  selectedResourcesChange = new EventEmitter<IIndicator[]>();

  selectedResources: IIndicator[] = [
    {
      name: '',
      quantity: 0,
      unity: '',
      pontuation: 0
    }
  ];

  unities: string[] = ['Litros', 'M3', 'Folhas', 'KWH', 'Kg'];

  constructor(
    public translate: TranslateService, 
    private sustainabilityService: SustainabilityService
  ) {}

  ngOnInit(): void {
    this.indicatorsTranslated = this.indicators?.map(indicator => {
      const translation: {value: string, name:string} = {} as {value: string, name:string};

      translation.name = this.translateIndicator(indicator);
      translation.value = indicator;

      return translation;
    });

    this.setResourcesUpdated()
  }

  setResourcesUpdated() {
    if(this.resourcesUpdated.length) {
      this.selectedResources = this.resourcesUpdated
    }
  }

  private calculationMapping: { [indicator: string]: CalculationFunction } = {
    'drinking water': this.calculateDrinkingWater,
    'pw water': this.calculatePWWater,
    'wfi water': this.calculateWFIWater,
    'energy': this.calculateEnergy,
    'paper': this.calculatePaper,
    'metal': this.calculateMetal,
    'plastic': this.calculatePlastic,
    'glass': this.calculateGlass,
    'rubber': this.calculateRubber,
    'danger Residue': this.calculateDangerResidue,
    'glp': this.calculateGLP,
    'gn': this.calculateGN,
    'diesel': this.calculateDiesel,
    'gas': this.calculateGas,
    'alcohol': this.calculateAlcohol
  };

  addResource() {
    this.selectedResources.push({
      name: '',
      quantity: 0,
      unity: '',
      pontuation: 0
    });

    this.emitChanges();
  }

  async removeResource(index: number, id: string) {
    this.selectedResources.splice(index, 1);
    this.emitChanges();
    
    if(id) {
      try {
        await this.onDeleteResource(id)
      } catch {
        console.error('Erro ao deletar item de sustentabilidade')
      }
    }
  }

  updateResource() {
    this.emitChanges();
  }

  emitChanges() {
    this.calculate([...this.selectedResources]);
    this.selectedResourcesChange.emit([...this.selectedResources]);
  }

  translateIndicator(indicator: string): string {
    switch (indicator) {
      case "drinking water":
        return this.translate.instant("drinking water");
      case "pw water":
        return this.translate.instant("pw water");
      case "wfi water":
        return this.translate.instant("wfi water");
      case "energy":
        return this.translate.instant("energy");
      case "paper":
        return this.translate.instant("paper");
      case "metal":
        return this.translate.instant("metal");
      case "plastic":
        return this.translate.instant("plastic");
      case "glass":
        return this.translate.instant("glass");
      case "rubber":
        return this.translate.instant("rubber");
      case "danger Residue":
        return this.translate.instant("danger Residue");
      case "glp":
        return this.translate.instant("glp");
      case "gn":
        return this.translate.instant("gn");
      case "diesel":
        return this.translate.instant("diesel");
      case "gas":
        return this.translate.instant("gas");
      case "alcohol":
        return this.translate.instant("alcohol");
      default:
        return indicator
      }
  }

  calculate(resources: IIndicator[]) {
    resources.forEach(resource => {
      const calculateFunction = this.calculationMapping[resource?.name?.toLowerCase()];

      if (calculateFunction) {
        resource.pontuation = calculateFunction(resource?.quantity, resource?.unity);
      }
    });

    this.selectedResourcesChange.emit([...resources]);
  }

  calculateDrinkingWater(quantity: number, unity: string): number {
    let result = 0;

    if(unity === 'M3') {
      result = (quantity * 0.2) / 1000;
    } else if(unity === 'Litros') {
      result = (quantity * 0.2) / 1000000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculatePWWater(quantity: number, unity: string): number {
    let result = 0;
    
    if(unity === 'M3') {
      result = (quantity * 0.5) / 1000;
    } else if(unity === 'Litros') {
      result = (quantity * 0.5) / 1000000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateWFIWater(quantity: number, unity: string): number {
    let result = 0;
    
    if(unity === 'M3') {
      result = (quantity * 0.8) / 1000;
    } else if(unity === 'Litros') {
      result = (quantity * 0.8) / 1000000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateEnergy(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'KWH') {
      result = quantity * 0.5;
    }

    return parseFloat(result.toFixed(2));
  }

  calculatePaper(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = (quantity * 1.5) / 1000;
    } else if(unity === 'Folhas') {
      result = ((quantity / 0.00000467775) * 1.5) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateMetal(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = quantity * 5.5;
    }

    return parseFloat(result.toFixed(2));
  }

  calculatePlastic(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = quantity * 3.5;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateGlass(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = quantity * 1.2;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateRubber(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = quantity * 10;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateDangerResidue(quantity: number, unity: string): number {
    let result = 0;
    

    if(unity === 'Kg') {
      result = quantity;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateGLP(quantity: number, unity: string): number{
    let result = 0;
    

    if(unity === 'M3') {
      result = quantity * 2;
    } else if(unity === 'Litros') {
      result = (quantity * 2) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateGN(quantity: number, unity: string): number{
    let result = 0;
    

    if(unity === 'M3') {
      result = quantity * 0.5;
    } else if(unity === 'Litros') {
      result = (quantity * 0.5) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateDiesel(quantity: number, unity: string): number{
    let result = 0;
    

   if(unity === 'Litros') {
      result = (quantity * 2.7) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateGas(quantity: number, unity: string): number{
    let result = 0;
    

   if(unity === 'Litros') {
      result = (quantity * 2.3) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  calculateAlcohol(quantity: number, unity: string): number{
    let result = 0;
    

   if(unity === 'Litros') {
      result = (quantity * 1.8) / 1000;
    }

    return parseFloat(result.toFixed(2));
  }

  async onDeleteResource(id: string): Promise<void> {
    await this.sustainabilityService.deleteSustainability(id)
  }
}
